import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, Transition as _Transition, withCtx as _withCtx, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "pill-avatar" }
const _hoisted_2 = { class: "avatar-initials" }
const _hoisted_3 = { class: "pill-name" }
const _hoisted_4 = {
  key: 0,
  class: "pill-status ringing"
}
const _hoisted_5 = {
  key: 1,
  class: "pill-duration"
}
const _hoisted_6 = { class: "tooltip-header" }
const _hoisted_7 = { class: "tooltip-title" }
const _hoisted_8 = { class: "speaker-label" }
const _hoisted_9 = { class: "message-text" }
const _hoisted_10 = {
  key: 0,
  class: "no-transcription"
}

import { computed, ref, watch, onBeforeUnmount, nextTick } from "vue";
import { Icon } from "@iconify/vue";

interface TranscriptionMessage {
  id: string | number;
  role: string;
  text: string;
}

interface Props {
  show: boolean;
  callerName?: string;
  callerPhoneNumber?: string;
  callDuration?: number;
  callState?: "ringing" | "active" | null;
  transcriptionMessages?: TranscriptionMessage[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MinimizedCallView',
  props: {
    show: { type: Boolean, default: false },
    callerName: { default: "" },
    callerPhoneNumber: { default: "" },
    callDuration: { default: 0 },
    callState: { default: null },
    transcriptionMessages: { default: () => [] }
  },
  emits: ["answer", "decline", "hangup", "maximize"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const pillPosition = ref({ x: window.innerWidth - 380, y: window.innerHeight - 80 });
const pillRef = ref<HTMLElement | null>(null);
const showTranscription = ref(false);
const tooltipContentRef = ref<HTMLElement | null>(null);

const pillStyle = computed(() => ({
  left: `${pillPosition.value.x}px`,
  top: `${pillPosition.value.y}px`,
}));

// Check if tooltip would go off right edge of screen
const tooltipOnLeft = computed(() => {
  const pillWidth = 280; // min-width of pill
  const tooltipWidth = 320;
  const gap = 12;
  const padding = 10;
  // If pill is positioned such that tooltip on right would overflow, show on left
  return pillPosition.value.x + pillWidth + gap + tooltipWidth + padding > window.innerWidth;
});

const callerInitials = computed(() => {
  if (props.callerName) {
    const names = props.callerName.trim().split(/\s+/);
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  }
  if (props.callerPhoneNumber) {
    const digits = props.callerPhoneNumber.replace(/\D/g, "");
    return digits.slice(-2) || "??";
  }
  return "??";
});

const formattedPhoneNumber = computed(() => {
  if (!props.callerPhoneNumber) return "Unknown";
  const cleaned = props.callerPhoneNumber.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return props.callerPhoneNumber;
});

const formattedCallDuration = computed(() => {
  if (props.callDuration > 0) {
    const hours = Math.floor(props.callDuration / 3600);
    const minutes = Math.floor((props.callDuration % 3600) / 60);
    const seconds = props.callDuration % 60;
    if (hours > 0) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return "00:00";
});

const startDrag = (event: MouseEvent | TouchEvent) => {
  const target = event.target as HTMLElement;
  // Don't drag when clicking buttons
  if (target.closest(".pill-btn") || target.closest(".transcription-tooltip")) {
    return;
  }

  event.preventDefault();
  isDragging.value = true;

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  dragStart.value = {
    x: clientX - pillPosition.value.x,
    y: clientY - pillPosition.value.y,
  };

  document.addEventListener("mousemove", handleDrag, { passive: false });
  document.addEventListener("mouseup", stopDrag);
  document.addEventListener("touchmove", handleDrag, { passive: false });
  document.addEventListener("touchend", stopDrag);
};

const handleDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return;

  event.preventDefault();

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  let newX = clientX - dragStart.value.x;
  let newY = clientY - dragStart.value.y;

  const pill = pillRef.value;
  if (pill) {
    const rect = pill.getBoundingClientRect();
    const padding = 10;
    newX = Math.max(padding, Math.min(newX, window.innerWidth - rect.width - padding));
    newY = Math.max(padding, Math.min(newY, window.innerHeight - rect.height - padding));
  }

  pillPosition.value = { x: newX, y: newY };
};

const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
};

const toggleTranscription = () => {
  showTranscription.value = !showTranscription.value;
};

const handleAnswer = () => {
  emit("answer");
};

const handleDecline = () => {
  emit("decline");
};

const handleHangup = () => {
  emit("hangup");
};

const handleMaximize = () => {
  emit("maximize");
};

// Auto-scroll transcription tooltip to bottom when new messages arrive
watch(
  () => props.transcriptionMessages,
  async () => {
    if (showTranscription.value && tooltipContentRef.value) {
      await nextTick();
      tooltipContentRef.value.scrollTop = tooltipContentRef.value.scrollHeight;
    }
  },
  { deep: true }
);

// Reset position when showing
watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      pillPosition.value = { x: window.innerWidth - 380, y: window.innerHeight - 80 };
    }
  }
);

onBeforeUnmount(() => {
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (__props.show)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          ref_key: "pillRef",
          ref: pillRef,
          class: _normalizeClass(["minimized-call-pill", { 'is-dragging': isDragging.value }]),
          style: _normalizeStyle(pillStyle.value),
          onMousedown: startDrag,
          onTouchstart: startDrag
        }, [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("span", _hoisted_2, _toDisplayString(callerInitials.value), 1)
          ]),
          _createElementVNode("div", {
            class: "pill-info",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (__props.callState === 'active' ? toggleTranscription() : handleMaximize()))
          }, [
            _createElementVNode("span", _hoisted_3, _toDisplayString(__props.callerName || formattedPhoneNumber.value), 1),
            (__props.callState === 'ringing')
              ? (_openBlock(), _createElementBlock("span", _hoisted_4, "Ringing..."))
              : (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(formattedCallDuration.value), 1))
          ]),
          (__props.callState === 'active')
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: _normalizeClass(["pill-btn transcription-btn", { active: showTranscription.value }]),
                onClick: _withModifiers(toggleTranscription, ["stop"]),
                title: "Toggle transcription"
              }, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:subtitles-outline",
                  width: "16",
                  height: "16"
                })
              ], 2))
            : _createCommentVNode("", true),
          (__props.callState === 'ringing')
            ? (_openBlock(), _createElementBlock("button", {
                key: 1,
                class: "pill-btn decline-btn",
                onClick: _withModifiers(handleDecline, ["stop"]),
                title: "Decline"
              }, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:phone-hangup",
                  width: "16",
                  height: "16"
                })
              ]))
            : _createCommentVNode("", true),
          (__props.callState === 'ringing')
            ? (_openBlock(), _createElementBlock("button", {
                key: 2,
                class: "pill-btn answer-btn",
                onClick: _withModifiers(handleAnswer, ["stop"]),
                title: "Answer"
              }, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:phone",
                  width: "16",
                  height: "16"
                })
              ]))
            : _createCommentVNode("", true),
          (__props.callState === 'active')
            ? (_openBlock(), _createElementBlock("button", {
                key: 3,
                class: "pill-btn hangup-btn",
                onClick: _withModifiers(handleHangup, ["stop"]),
                title: "Hangup"
              }, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:phone-hangup",
                  width: "16",
                  height: "16"
                })
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("button", {
            class: "pill-btn maximize-btn",
            onClick: _withModifiers(handleMaximize, ["stop"]),
            title: "Maximize"
          }, [
            _createVNode(_unref(Icon), {
              icon: "mdi:arrow-expand",
              width: "16",
              height: "16"
            })
          ]),
          _createVNode(_Transition, { name: "tooltip-fade" }, {
            default: _withCtx(() => [
              (showTranscription.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: _normalizeClass(["transcription-tooltip", { 'tooltip-left': tooltipOnLeft.value }]),
                    onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
                  }, [
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("span", _hoisted_7, [
                        _createVNode(_unref(Icon), {
                          icon: "mdi:subtitles-outline",
                          width: "14",
                          height: "14"
                        }),
                        _cache[3] || (_cache[3] = _createTextVNode(" Live Transcription ", -1))
                      ]),
                      _createElementVNode("button", {
                        class: "tooltip-close",
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (showTranscription.value = false))
                      }, [
                        _createVNode(_unref(Icon), {
                          icon: "mdi:close",
                          width: "14",
                          height: "14"
                        })
                      ])
                    ]),
                    _createElementVNode("div", {
                      class: "tooltip-content",
                      ref_key: "tooltipContentRef",
                      ref: tooltipContentRef
                    }, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.transcriptionMessages, (message, index) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `${message.id}-${index}`,
                          class: _normalizeClass(["transcription-message", message.role?.toLowerCase().replace(' ', '-')])
                        }, [
                          _createElementVNode("span", _hoisted_8, _toDisplayString(message.role) + ":", 1),
                          _createElementVNode("span", _hoisted_9, _toDisplayString(message.text), 1)
                        ], 2))
                      }), 128)),
                      (__props.transcriptionMessages.length === 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, " Waiting for transcription... "))
                        : _createCommentVNode("", true)
                    ], 512)
                  ], 2))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ], 38))
      : _createCommentVNode("", true)
  ]))
}
}

})